import { useMemo, useContext, useState } from "react";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../../../../utils/misc";
import SectionHeader from "../../shared/SectionHeader";
import TablePagination from "../../shared/TablePagination";
import { AppContext } from "./../../../../../context/app";
import {
  useGetUserAndLeadNotes,
  useDeleteNote,
} from "../../../../../apis/userApi.hooks";
import { convertIsoDateOrTimestampToUSDateAndTimeFormat } from "../../../helpers";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  getSortedRowModel,
} from "@tanstack/react-table";
import { Calendar, Clock, Plus, Trash2, MoreVertical } from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@pushpress/pushpress-ui";
import { EMPTY_VALUE, PAGINATION_PAGE_SIZE } from "../../../constants";
import ErrorState from "../../shared/ErrorState";
import TableSkeleton from "../../shared/TableSkeleton";
import AddNotesModal from "./AddNotesModal";
import ConfirmationModal from "../../shared/ConfirmationModal";

type NotesProps = {
  userUuid: string;
};

export default function Notes({ userUuid }: NotesProps) {
  const { client, user } = useContext(AppContext);
  const { data, isLoading, isError, refetch } = useGetUserAndLeadNotes(
    userUuid,
    client.uuid,
  );

  const [showAddNotes, setShowAddNotes] = useState<boolean>(false);
  const [uuidToDelete, setUuidToDelete] = useState<string>("");
  const [sorting] = useState<{ id: string; desc: boolean }[]>([
    { id: "created", desc: true },
  ]);
  const { mutate, isLoading: isDeletingNote } = useDeleteNote();
  const allowDeleteNoteAction = user.superuser || user.role === "admin";

  const convertToTableData = (data) => {
    if (!data) return [];
    const { leadFunnel, userNotes } = data;
    const notes =
      userNotes.map((userNote) => ({
        created: userNote.createTimestamp,
        note: userNote.note,
        createdBy: userNote.createdByFirstName
          ? `${userNote.createdByFirstName} ${userNote.createdByLastName}`
          : EMPTY_VALUE,
        uuid: userNote.uuid,
      })) ?? [];
    const leadNotes =
      leadFunnel.map((leadNote) => ({
        created: leadNote.timestamp,
        note: leadNote.notes,
        createdBy: leadNote.createdByFirstName
          ? `${leadNote.createdByFirstName} ${leadNote.createdByLastName}`
          : "System",
        uuid: null,
      })) ?? [];
    return notes.concat(leadNotes);
  };

  const onClose = () => setUuidToDelete("");
  const deleteNote = (uuid: string) => {
    mutate(
      { uuid },
      {
        onSuccess: () => {
          toast.success("Note deleted", defaultToastOptions);
          onClose();
        },
        onError: () => {
          toast.error(
            "There was an error deleting the note",
            defaultToastOptions,
          );
        },
      },
    );
  };
  const columns = useMemo(() => {
    return [
      {
        header: "Created",
        accessorKey: "created",
        cell: ({ row }) => {
          const { created } = row.original;
          const { date, time } =
            convertIsoDateOrTimestampToUSDateAndTimeFormat(created);
          return (
            <>
              <div className="tw-flex tw-items-center">
                <Calendar className="tw-h-3 tw-w-3 tw-mr-1" />
                {date}
              </div>
              <div className="tw-flex tw-items-center tw-text-sm tw-text-neutral-600">
                <Clock className="tw-h-3 tw-w-3 tw-mr-1" /> {time}
              </div>
            </>
          );
        },
      },
      {
        header: "Note",
        accessorKey: "note",
      },
      {
        header: "Created By",
        accessorKey: "createdBy",
      },
      {
        id: "actions",
        accessorKey: "actions",
        header: null,
        cell: ({ row }) => {
          return (
            <TableCell key={row.id} className="tw-flex tw-justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    data-testid="row-notes-actions-button"
                  >
                    <MoreVertical className="tw-h-5 tw-w-5 max-sm:tw-p-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    data-testid="delete-note-button"
                    onClick={() => setUuidToDelete(row.original.uuid)}
                    disabled={!allowDeleteNoteAction || !row.original.uuid}
                    className="tw-text-error tw-cursor-pointer"
                  >
                    <Trash2 className="tw-h-4 tw-w-4 tw-mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          );
        },
      },
    ];
  }, [allowDeleteNoteAction]);

  const tableData = useMemo(() => convertToTableData(data), [data]);
  const table = useReactTable({
    data: tableData,
    columns,
    initialState: {
      pagination: {
        pageSize: PAGINATION_PAGE_SIZE,
      },
    },
    state: { sorting },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const showPagination = !isLoading && tableData.length > PAGINATION_PAGE_SIZE;
  return (
    <div className="tw-space-y-4">
      <AddNotesModal
        userUuid={userUuid}
        showAddNotes={showAddNotes}
        setShowAddNotes={setShowAddNotes}
      />
      <ConfirmationModal
        open={!!uuidToDelete}
        onOpenChange={onClose}
        title="Delete note"
        content="Are you sure you want to delete this user note?"
        onClose={onClose}
        onConfirm={() => deleteNote(uuidToDelete)}
        confirmationButtonText="Confirm"
        isLoading={isDeletingNote}
      />

      <SectionHeader
        title="Notes"
        actions={
          <div className="tw-flex tw-items-center tw-gap-2">
            <Button onClick={() => setShowAddNotes(true)} size="sm">
              <Plus className="tw-mr-1 tw-h-4 tw-w-4" />
              Add Note
            </Button>
          </div>
        }
      />
      <div className="tw-rounded-lg tw-border tw-p-4 tw-bg-white tw-flex tw-flex-col tw-gap-4">
        <div className="tw-rounded tw-ring-1 tw-ring-black/5 tw-mt-2">
          {isError ? (
            <ErrorState refetch={refetch} />
          ) : isLoading ? (
            <TableSkeleton
              numCols={columns.length}
              numRows={PAGINATION_PAGE_SIZE}
            />
          ) : (
            <Table className="tw-divide-y tw-divide-neutral-300">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id} className="tw-bg-neutral-50">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody className="tw-divide-y tw-divide-neutral-200">
                {table.getRowModel()?.rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <TableCell key={cell.id} className="tw-py-0.5">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="tw-h-24 tw-text-center"
                    >
                      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-8 tw-text-center">
                        <h4 className="tw-mb-2">No notes for this user</h4>
                        <p className="tw-text-neutral-500 tw-mb-6">
                          Add a note to get started
                        </p>
                        <div className="tw-flex tw-gap-4">
                          <Button
                            onClick={() => setShowAddNotes(true)}
                            size="sm"
                          >
                            <Plus className="tw-mr-1 tw-h-4 tw-w-4" />
                            Add Note
                          </Button>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
        {showPagination && <TablePagination table={table} />}
      </div>
    </div>
  );
}
